import { type AppConfigBase, type Option } from 'ecosystem'
import storefrontApi from 'api/storefront-api'
import { notFound } from 'next/navigation'
import { type OrderProvisionResult } from 'api/storefront-api/ordersEndpoint'
import { createLogger } from 'shared-utils'

const logger = createLogger('order')

export const getServerSidePropsConfirmationPage = async ({
  searchParams,
  appConfig
}: {
  appConfig: AppConfigBase
  searchParams?: Record<string, string | string[] | undefined>
}) => {
  const orderId = searchParams?.order_id as string | undefined
  const invoiceOrderNumber = searchParams?.invoiceOrder as string | undefined

  let provisionResult: Option<OrderProvisionResult> = null
  let provisionError: string | null = null

  // INVOICE confirmations don't go through provider provisioning — the order
  // is already in CREATED by the time we hit /confirmation. Look up directly.
  if (invoiceOrderNumber) {
    try {
      const { data: invoiceOrder, error } = await storefrontApi.invoiceOrders.getInvoiceOrder({
        orderNumber: invoiceOrderNumber,
        includeItems: true,
        includeCustomer: true
      })
      if (error || !invoiceOrder) {
        provisionError =
          typeof error === 'string'
            ? error
            : ((error as { message?: string } | undefined)?.message ?? 'invoice-order-not-found')
      } else {
        provisionResult = {
          storefrontOrder: invoiceOrder,
          paymentProviderResult: undefined
        }
      }
    } catch (e) {
      provisionError = e instanceof Error ? e.toString() : 'unknown'
    }

    return { order: provisionResult, provisionError }
  }

  switch (appConfig.paymentProviderConfig?.providerName ?? 'KLARNA') {
    case 'KLARNA':
    case 'NEXI':
    case 'QLIRO':
    case 'LEDYER':
    case 'SVEA':
    case 'BRIQPAY':
      if (!orderId) {
        return notFound()
      }

      try {
        provisionResult = await storefrontApi.orders.provisionOrder(
          orderId || '',
          appConfig.paymentProviderConfig,
          appConfig.shippingProviderConfig,
          // Neither SVEA's ConfirmationUri nor Briqpay's urls.redirect can carry the
          // provider-side id (no template support), so both hardcode the SF orderNumber
          // there. Their provisioning providers read the SF order first and resolve the
          // provider id from its persisted paymentId. For everyone else identifierKind is
          // ignored — the orderId param is already the Klarna/Nexi/Qliro payment-side id
          // from their redirect.
          {
            identifierKind: ['SVEA', 'BRIQPAY'].includes(
              appConfig.paymentProviderConfig?.providerName ?? ''
            )
              ? 'orderNumber'
              : 'paymentId'
          }
        )
      } catch (e) {
        if (e instanceof Error) {
          provisionError = e.toString()
        } else {
          provisionError = 'unknown'
        }
      }

      break
  }

  logger.log(
    'getServerSidePropsConfirmationPage params',
    `orderId = ${orderId}`,
    `appConfig.paymentProviderConfig = `,
    appConfig.paymentProviderConfig,
    `appConfig.shippingProviderConfig  = `,
    appConfig.shippingProviderConfig
  )

  if (provisionError) {
    logger.error('getServerSidePropsConfirmationPage provisionError = ', provisionError)
  } else {
    logger.log('getServerSidePropsConfirmationPage provisionResult = ', provisionResult)
  }

  return {
    order: provisionResult,
    provisionError
  }
}
